/**
 * Custom Hook: useInactivityTimeout
 * Manages inactivity timeout with warning dialog
 * 
 * Features:
 * - Tracks user activity
 * - Shows warning before timeout
 * - Triggers logout on inactivity
 * - Configurable via backend
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  startActivityTracking, 
  stopActivityTracking, 
  resetActivity 
} from '../utils/activityTracker';
import { 
  startInactivityMonitor, 
  stopInactivityMonitor, 
  extendSession 
} from '../utils/inactivityMonitor';

/**
 * Hook to manage inactivity timeout
 * @param {Function} onLogout - Callback when user should be logged out
 * @param {number} timeoutMinutes - Inactivity timeout in minutes (default: 30)
 * @param {string} channelName - BroadcastChannel name for sync (default: digitalvault-session)
 * @param {boolean} enabled - Whether inactivity tracking is enabled
 * @returns {Object} - Warning state and handlers
 */
export const useInactivityTimeout = (
  onLogout,
  timeoutMinutes = 30,
  channelName = 'digitalvault-session',
  enabled = true
) => {
  const [showWarning, setShowWarning] = useState(false);
  const [warningSeconds, setWarningSeconds] = useState(60);

  // Handle inactivity warning
  const handleWarning = useCallback((secondsRemaining) => {
    setWarningSeconds(secondsRemaining);
    setShowWarning(true);
  }, []);

  // Handle auto-logout
  const handleAutoLogout = useCallback(() => {
    setShowWarning(false);
    if (onLogout) {
      onLogout();
    }
  }, [onLogout]);

  // Handle "Stay Logged In" button
  const handleStayLoggedIn = useCallback(() => {
    extendSession();
    setShowWarning(false);
  }, []);

  // Start tracking when enabled
  useEffect(() => {
    if (enabled) {
      // Initialize tracking with role-specific timeout and channel
      startActivityTracking(timeoutMinutes, channelName);
      startInactivityMonitor(handleAutoLogout, handleWarning);

      return () => {
        // Cleanup on unmount or disable
        stopActivityTracking();
        stopInactivityMonitor();
      };
    }
  }, [enabled, timeoutMinutes, channelName, handleAutoLogout, handleWarning]);

  return {
    showWarning,
    warningSeconds,
    handleStayLoggedIn,
    handleAutoLogout,
    resetActivity
  };
};

export default useInactivityTimeout;
